import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadDocuments = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [appInfo, setAppInfo] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('other');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/application/${token}`),
      axios.get(`${API}/document-categories`),
    ])
      .then(([appRes, catRes]) => {
        setAppInfo(appRes.data);
        setCategories(catRes.data.categories);
        setLoading(false);
      })
      .catch(() => {
        setError('Invalid or expired document upload link.');
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/application/${token}/documents`)
      .then(res => {
        setDocuments(res.data.documents.map(d => ({
          id: d.id,
          filename: d.filename,
          category: '',
        })));
      })
      .catch(() => {});
  }, [token]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);

      const response = await axios.post(
        `${API}/application/${token}/document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        const catLabel = categories.find(c => c.key === selectedCategory)?.label || selectedCategory;
        setDocuments(prev => [...prev, {
          id: response.data.documentId,
          filename: response.data.filename,
          category: catLabel,
        }]);
        toast.success('Document uploaded successfully');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-slate-200 text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-slate-800 p-12 rounded-2xl shadow-2xl text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-slate-100 mb-4">Invalid Link</h2>
          <p className="text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  const companyName = appInfo?.company?.name || 'Your Application';
  const appId = appInfo?.paysecApplicationId || token;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-3xl font-bold text-slate-100 mb-2">Upload Documents</h1>
          <p className="text-slate-400 text-lg">{companyName}</p>
          <p className="text-slate-500 text-sm mt-1">Application ID: <span className="font-mono">{appId}</span></p>
        </header>

        <div className="bg-slate-800 p-6 rounded-lg mb-6">
          <p className="text-slate-300 mb-6">
            Upload additional documents requested by the underwriting team. Select the document type before uploading.
          </p>

          <div className="space-y-6">
            <div>
              <Label className="text-slate-200 mb-3 block">Document Type</Label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-500"
              >
                {categories.map(cat => (
                  <option key={cat.key} value={cat.key}>{cat.label}</option>
                ))}
                {categories.length === 0 && <option value="other">Other</option>}
              </select>
            </div>

            <div>
              <Label className="text-slate-200 mb-3 block">Upload File</Label>
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-slate-500 transition-colors">
                <input
                  type="file"
                  id="doc-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  accept=".jpg,.jpeg,.png,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.msg,.heic,.heif"
                />
                <label htmlFor="doc-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-slate-300 mb-2">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-sm text-slate-500">PDF, PNG, JPG, DOC, XLS up to 10MB</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {documents.length > 0 && (
          <div className="bg-slate-800 p-6 rounded-lg mb-6">
            <Label className="text-slate-200 mb-3 block">Uploaded Documents</Label>
            <div className="space-y-2">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-slate-900 p-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="text-slate-200">{doc.filename}</span>
                      {doc.category && <span className="text-slate-500 text-sm ml-3">{doc.category}</span>}
                    </div>
                  </div>
                  <span className="text-green-400 text-sm">Uploaded</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-slate-800/50 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-slate-200 mb-4">Commonly Requested Documents</h4>
          <ul className="space-y-2 text-slate-300">
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Corporation documents (business license, formation documents)</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Voided check or bank letter</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Driver's license for all principals</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Bank statements (last 3 months)</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Processing statements (if currently processing)</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>Tax returns</span></li>
            <li className="flex items-start"><span className="mr-2">&bull;</span><span>PCI compliance documentation</span></li>
          </ul>
        </div>

        <div className="mt-10 pt-8 border-t border-slate-700/50">
          <div className="flex flex-col items-center gap-4">
            <img src="/paysec-logo.png" alt="PaySec" className="h-8 opacity-80" />
            <p className="text-slate-400 text-sm italic">
              Copyright &copy; {new Date().getFullYear()} &mdash; PaySec LLC. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDocuments;
